import { isVideo, ProductType } from '@headless-workspace/domain/common/client';
import {
    CapacityFormat,
    CustomerBasketValue,
    isProductSet,
    ProductDetailValue,
    PromotionType,
} from '@headless-workspace/domain/common/server';
import { ArrayHelpers, ValueHelpers } from '@headless-workspace/utils';

const ENV_TEMPLATE_FOR_STANDARD_PRODUCT = 'product page';
const ENV_TEMPLATE_FOR_PRODUCT_SET = 'product page set look';

const BREADCRUMB_ITEM_SEPARATOR = '/';

const NO_PROMOTION = 'without promotion';
const CROSS_OUT_PRICE = 'crossed out price';

const getProductBreadcrumbLabels = (breadcrumbs: ProductDetailValue['breadcrumb']): string[] => {
    return breadcrumbs.map((breadcrumb) => breadcrumb.tcDisplayName).filter((tcDisplayName) => tcDisplayName !== '');
};

const getProductNumRating = (reviewCount: number) => {
    if (reviewCount === 0) return '0';
    if (reviewCount <= 10) return '0-10';
    if (reviewCount <= 25) return '11-25';
    if (reviewCount <= 50) return '26-50';
    if (reviewCount <= 75) return '51-75';

    return '76+';
};

const formattedVariables = (product: ProductDetailValue, customerBasket?: CustomerBasketValue) => {
    const envTemplate =
        product.type !== ProductType.PRODUCT_SET ? ENV_TEMPLATE_FOR_STANDARD_PRODUCT : ENV_TEMPLATE_FOR_PRODUCT_SET;

    // Represents the parent identifier of a product. In the case of PRODUCT_SET, this will be the PRODUCT_SET id, in other cases, it will be the masterId.
    const parentId = product.type !== ProductType.PRODUCT_SET ? product.masterId : product.productSetId;

    const customized = ArrayHelpers.matches(customerBasket?.products, (basketProduct) => {
        if (isProductSet(product)) {
            return false;
        }

        return (
            basketProduct?.productId === product.sku &&
            Array.isArray(basketProduct.engraves) &&
            basketProduct.engraves.length > 0
        );
    });

    const breadcrumbIds = product.breadcrumb.slice(1).map((item) => item.id);
    const breadcrumbLabels = getProductBreadcrumbLabels(product.breadcrumb);
    const breadcrumbJoined = breadcrumbLabels.join(BREADCRUMB_ITEM_SEPARATOR);
    const [topCat, cat, subCat] = breadcrumbLabels;

    const productPromotion =
        !product.promotion || product.promotion === PromotionType.REGULAR ? NO_PROMOTION : CROSS_OUT_PRICE;

    return {
        envTemplate,
        brandName: product.brand.name.toLowerCase(),
        breadcrumbIds,
        breadcrumbJoined,
        currency: product.currency.toLowerCase(),
        isCustomizable: ValueHelpers.mapYesNo(product.isEngravable),
        isCustomized: customized ? 1 : 0,
        inStock: ValueHelpers.mapYesNo(!!product.stockLevel),
        hasDiscount: ValueHelpers.mapYesNo(!!(product.priorPrice || product.originalPrice)),
        hasTravelSize: ValueHelpers.mapYesNo(product.formats.includes(CapacityFormat.Mini)),
        hasVideo: ValueHelpers.mapYesNo(product.medias.some((media) => isVideo(media))),
        pageCategory: {
            topCat: topCat ?? '',
            cat: cat ?? '',
            subCat: subCat ?? '',
        },
        productAxis: product.axis?.toLowerCase(),
        pageName: `${product.name?.full}|${parentId.toLowerCase()}`,
        productNature: product.nature?.toLowerCase(),
        productPid: product.type !== ProductType.PRODUCT_SET ? product.masterId : undefined,
        productPromotion,
        productRange: product.range?.toLowerCase(),
        productSku: product.type !== ProductType.PRODUCT_SET ? product.sku : undefined,
        reviewCountRange: getProductNumRating(product.reviewCount),
        skuName: product.type !== ProductType.PRODUCT_SET ? `${product.sku} - ${product.name?.full}` : undefined,
    };
};

export const ProductDetailDataLayerUtils = {
    formattedVariables,
};
